import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const FORM_URL = 'https://readdy.ai/api/form/d9n1itgbdkpk99j7gfig';

const faqData = [
  {
    q: '51视频的影视资源来自哪里？',
    a: '51视频聚合了香蕉视频、红桃视频、91视频等多个热门影视平台的资源，为用户提供一站式的影视观看体验。我们通过先进的资源整合技术，确保内容的丰富性和多样性。',
  },
  {
    q: '51视频是否需要付费观看？',
    a: '51视频目前所有内容均免费提供在线观看服务，无需注册、无需付费。我们致力于为广大用户提供便捷、高质量的免费观影体验。',
  },
  {
    q: '为什么有些视频播放卡顿？',
    a: '51视频采用全球CDN加速技术，正常情况下播放流畅不卡顿。如遇卡顿，建议检查您的网络连接，或尝试切换清晰度。我们的技术团队会持续优化播放体验。',
  },
  {
    q: '51视频支持哪些设备观看？',
    a: '51视频完美适配电脑、平板和手机等多终端设备。无论您使用Windows、macOS、Android还是iOS系统，都能获得一致的优质观影体验。',
  },
  {
    q: '如何联系51视频的客服团队？',
    a: '您可以通过本页面的联系表单或发送邮件至我们的官方邮箱与我们取得联系。我们的客服团队会在24小时内回复您的咨询。',
  },
  {
    q: '51视频的资源更新频率如何？',
    a: '51视频每日更新海量影视资源，包括最新上映的电影、热播电视剧、综艺节目以及动漫新番。我们的内容团队全天候监控各平台动态，确保第一时间为用户带来最新内容。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 51视频 | 品牌介绍 | 联系方式 | 用户口碑"
        description="51视频品牌介绍：聚合香蕉视频、红桃视频、91视频等热门影视资源，提供最新电影、电视剧、综艺免费在线观看。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="51视频,关于我们,品牌介绍,联系方式,用户口碑,影视平台"
        canonicalUrl="https://51video.example.com/about"
      />
      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - 51视频',
        description: '51视频品牌介绍、联系方式与用户口碑',
        url: 'https://51video.example.com/about',
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51video.example.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://51video.example.com/about' },
        ],
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              {/* Hero */}
              <section className="text-center py-12 md:py-16" aria-label="品牌介绍">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                  关于 <span className="text-primary-500">51视频</span>
                </h1>
                <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                  51视频是一个聚合香蕉视频、红桃视频、91视频等热门影视资源的免费在线观影平台。
                  我们致力于为中文用户群体提供最新、最全、最高清的影视内容，打造极致的在线观影体验。
                </p>
              </section>

              {/* Brand Story */}
              <section className="pb-14 md:pb-20" aria-label="品牌故事">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">我们的故事</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      51视频诞生于对影视内容的热爱和对用户体验的极致追求。在数字娱乐高速发展的时代，
                      我们注意到用户在寻找高质量影视内容时面临的碎片化问题——需要在多个平台之间频繁切换，
                      忍受各种广告和缓冲等待。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      因此，我们创立了51视频——一个聚合香蕉视频、红桃视频、91视频等主流影视平台资源的综合观影站点。
                      通过智能资源整合和全球CDN加速技术，我们为用户提供一站式、高清流畅的影视观看服务。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      自上线以来，51视频已累计服务超过数百万用户，每日更新海量影视资源，
                      涵盖电影、电视剧、综艺、动漫、纪录片等多种类型，满足不同用户的多元化观影需求。
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden aspect-video bg-background-200">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20team%20collaborating%20on%20digital%20entertainment%20project%2C%20warm%20natural%20lighting%2C%20creative%20workspace%20with%20large%20screens%20showing%20video%20content%2C%20professional%20yet%20relaxed%20atmosphere%2C%20editorial%20photography%20style&width=800&height=450&seq=about-brand-story&orientation=landscape"
                      alt="51视频团队"
                      title="51视频 - 我们的团队"
                      className="w-full h-full object-cover"
                      width="800"
                      height="450"
                    />
                  </div>
                </div>
              </section>

              {/* Milestones */}
              <section className="pb-14 md:pb-20" aria-label="发展历程">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">发展历程</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { year: '2022', title: '平台创立', desc: '51视频正式上线，聚合主流影视平台资源，开启一站式观影服务' },
                    { year: '2023', title: '快速成长', desc: '用户突破百万，资源库扩充至万部以上，覆盖电影、电视剧、综艺等全品类' },
                    { year: '2024', title: '技术升级', desc: '全面升级CDN加速与4K高清播放技术，实现更流畅的观影体验' },
                    { year: '2025', title: '行业领先', desc: '成为中文影视聚合领域的领先平台，持续优化产品与服务体验' },
                  ].map((m, idx) => (
                    <div key={idx} className="bg-background-100 rounded-lg p-6 text-center relative">
                      <div className="w-12 h-12 bg-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-white font-bold text-sm">{m.year}</span>
                      </div>
                      <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">{m.title}</h4>
                      <p className="text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Reputation */}
              <section className="pb-14 md:pb-20" aria-label="用户口碑">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">用户口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { name: '张先生', rating: 5, text: '一直在用51视频追剧，资源真的很全，狂飙、繁花这些热播剧更新特别快，而且播放不卡顿，太赞了！' },
                    { name: '李女士', rating: 5, text: '之前要在好几个APP之间换来换去，现在一个51视频就搞定了。电影、综艺、动漫应有尽有，画质也超棒。' },
                    { name: '王同学', rating: 4, text: '作为动漫迷，51视频的动漫资源真的让我惊喜。斗破苍穹、完美世界这些年番更新很及时，追番必备！' },
                  ].map((review) => (
                    <div key={review.name} className="bg-background-100 rounded-lg p-6">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className={`ri-star-fill text-sm ${i < review.rating ? 'text-accent-500' : 'text-background-300'}`}></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.text}</p>
                      <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ */}
              <section className="pb-14 md:pb-20" aria-label="常见问题">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">常见问题</h2>
                <div className="max-w-3xl mx-auto space-y-3">
                  {faqData.map((faq, idx) => (
                    <details key={idx} className="group bg-background-100 rounded-lg overflow-hidden">
                      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors">
                        {faq.q}
                        <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-6 pb-4">
                        <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* Contact Form */}
              <section className="pb-14 md:pb-20" aria-label="联系我们">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">联系我们</h2>
                <div className="max-w-lg mx-auto bg-background-100 rounded-lg p-8">
                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-check-line text-accent-500 text-3xl"></i>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">提交成功</h3>
                      <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复！</p>
                      <button
                        onClick={() => setFormStatus('idle')}
                        className="mt-4 px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        继续留言
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} id="contact-form" className="space-y-5" data-readdy-form="">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 bg-white border border-background-300 rounded-lg text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 bg-white border border-background-300 rounded-lg text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 bg-white border border-background-300 rounded-lg text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入留言主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={5}
                          required
                          maxLength={500}
                          className="w-full px-4 py-2.5 bg-white border border-background-300 rounded-lg text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（不超过500字）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                      </div>
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500">{formError}</p>
                      )}
                      <button
                        type="submit"
                        className="w-full py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        提交留言
                      </button>
                    </form>
                  )}
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}